import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Building2,
  FileText,
  Link2,
  Leaf,
  Shield,
  ChevronRight,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || '';

const CATEGORY_ICONS = {
  suggestions: Building2,
  edits: FileText,
  link_requests: Link2,
  sustainability: Leaf,
  flagged: Shield
};

const CATEGORY_COLORS = {
  suggestions: 'text-blue-500',
  edits: 'text-amber-500',
  link_requests: 'text-purple-500',
  sustainability: 'text-green-500',
  flagged: 'text-red-500'
};

export const AdminNotifications = ({ onTabChange }) => {
  const [notifications, setNotifications] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/notifications`);
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Refresh every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleCategoryClick = (category) => {
    setOpen(false);
    // Map notification categories to actual admin tab values
    const tabMap = {
      suggestions: 'suggestions',
      edits: 'vendor-edits',
      link_requests: 'link-requests',
      sustainability: 'sustainability',
      flagged: 'moderation'
    };
    if (onTabChange && tabMap[category]) {
      onTabChange(tabMap[category]);
    }
  };

  const totalCount = notifications?.total || 0;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="admin-notifications-bell"
        >
          <Bell className="w-5 h-5" />
          {totalCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full animate-pulse">
              {totalCount > 99 ? '99+' : totalCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {totalCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {totalCount} pending
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : totalCount === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
            <p className="text-sm font-medium text-foreground">All caught up!</p>
            <p className="text-xs text-muted-foreground">No pending items to review</p>
          </div>
        ) : (
          <>
            {Object.entries(notifications?.categories || {}).map(([key, category]) => {
              if (category.count === 0) return null;
              
              const Icon = CATEGORY_ICONS[key] || Bell;
              const colorClass = CATEGORY_COLORS[key] || 'text-primary';
              
              return (
                <DropdownMenuItem
                  key={key}
                  className="flex items-center justify-between cursor-pointer py-3"
                  onClick={() => handleCategoryClick(key)}
                  data-testid={`notification-${key}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn("p-1.5 rounded-md bg-muted", colorClass)}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{category.label}</p>
                      {category.recent?.[0] && (
                        <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                          Latest: {category.recent[0].company_name || category.recent[0].platform_name || 'New item'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="secondary" 
                      className={cn(
                        "text-xs",
                        category.count > 5 ? "bg-red-100 text-red-700" : ""
                      )}
                    >
                      {category.count}
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </DropdownMenuItem>
              );
            })}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
